import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import {
	CheckBox,
	Stepper,
	TextBox,
	ToggleButton,
	react,
	useRefWithSetter,
} from "uikit";
import { FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useObjectEditor from "../../../../../../hooks/useObjectEditor";
import PaymentSystem, {
	PaymentSystemFrontEndType,
} from "../../../../../../services/PaymentSystem";
import {
	StyledColumn,
	StyledRow,
	StyledP,
} from "../../../../../../components/common";
import translationPath from "../../constants/translationPath";
import ValueStepper from "../ValueStepper";

import InternalController from "./Controller";
import PlatonPaymentsCurrency from "./components/PlatonPaymentsCurrency";
import PlatonPaymentsCountryIso from "./components/PlatonPaymentsCountryIso";

const PlatonPaymentBase = react.withController<
	PlatonPayment.PropsBase,
	PlatonPayment.Controller
>(({ value, onChange, controller }) => {
	const { t } = useTranslation();

	const [countryIsoRef, setCountryIsoRef] =
		useRefWithSetter<PlatonPaymentsCountryIso.Ref | null>(null);
	const [currencyRef, setCurrencyRef] =
		useRefWithSetter<PlatonPaymentsCurrency.Ref | null>(null);

	const valueEditor = useObjectEditor(value, onChange);

	const type = valueEditor.useGetter("type");

	const isExecutor = useMemo(
		() =>
			PaymentSystemFrontEndType.EXECUTOR === type ||
			PaymentSystemFrontEndType.TEMPLATE_EXECUTOR === type,
		[type],
	);

	const isCustomer = useMemo(
		() =>
			PaymentSystemFrontEndType.CUSTOMER === type ||
			PaymentSystemFrontEndType.TEMPLATE_CUSTOMER === type,
		[type],
	);

	// platonClientKey
	const platonClientKey = valueEditor.useGetter("platonClientKey");
	const setPlatonClientKey = valueEditor.useSetter("platonClientKey");
	const [errorPlatonClientKey, setPlatonClientKeyError] =
		useState<boolean>(false);
	const handlePlatonClientKey = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setPlatonClientKey(value);
				setPlatonClientKeyError(false);
			}
		},
		[setPlatonClientKey, setPlatonClientKeyError],
	);

	const textErrorPlatonClientKey = useMemo(() => {
		if (!errorPlatonClientKey) return "";
		return t([
			`${translationPath}.modal.errors.platonClientKey`,
			"Client key not entered",
		]);
	}, [t, errorPlatonClientKey]);

	const placeholderPlatonClientKey = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.platonClientKey`,
				"Enter client key",
			]),
		[t],
	);

	const textPlatonClientKey = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.platonClientKey`,
				"Client key (API key)",
			]),
		[t],
	);

	// platonClientPassword
	const platonClientPassword = valueEditor.useGetter("platonClientPassword");
	const setPlatonClientPassword = valueEditor.useSetter(
		"platonClientPassword",
	);
	const [errorPlatonClientPassword, setPlatonClientPasswordError] =
		useState<boolean>(false);
	const handlePlatonClientPassword = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setPlatonClientPassword(value);
				setPlatonClientPasswordError(false);
			}
		},
		[setPlatonClientPassword, setPlatonClientPasswordError],
	);

	const textErrorPlatonClientPassword = useMemo(() => {
		if (!errorPlatonClientPassword) return "";
		return t([
			`${translationPath}.modal.errors.platonClientPassword`,
			"Client password not entered",
		]);
	}, [t, errorPlatonClientPassword]);

	const placeholderPlatonClientPassword = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.platonClientPassword`,
				"Enter client password",
			]),
		[t],
	);

	const textPlatonClientPassword = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.platonClientPassword`,
				"Client password",
			]),
		[t],
	);

	// platonApiUrl
	const platonApiUrl = valueEditor.useGetter("platonApiUrl");
	const setPlatonApiUrl = valueEditor.useSetter("platonApiUrl");
	const [errorPlatonApiUrl, setPlatonApiUrlError] = useState<boolean>(false);
	const handlePlatonApiUrl = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setPlatonApiUrl(value);
				setPlatonApiUrlError(false);
			}
		},
		[setPlatonApiUrl],
	);

	const textErrorPlatonApiUrl = useMemo(() => {
		if (!errorPlatonApiUrl) return "";
		return t([
			`${translationPath}.modal.errors.platonApiUrl`,
			"API URL not entered",
		]);
	}, [t, errorPlatonApiUrl]);

	const placeholderPlatonApiUrl = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.platonApiUrl`,
				"e.g. https://secure.platononline.com",
			]),
		[t],
	);

	const textPlatonApiUrl = useMemo(
		() => t([`${translationPath}.modal.text.platonApiUrl`, "API URL"]),
		[t],
	);

	const textPlatonFeeFromCallbackHint = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.platonFeeFromCallbackHint`,
				"Actual Platon commission is taken from the payment callback (fee field) at order close.",
			]),
		[t],
	);

	// platonFeePercentage
	const platonFeePercentageValue = valueEditor.useGetter(
		"platonFeePercentage",
	);
	const setPlatonFeePercentageValue = valueEditor.useSetter(
		"platonFeePercentage",
	);
	const handlePlatonFeePercentage = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setPlatonFeePercentageValue(value);
			}
		},
		[setPlatonFeePercentageValue],
	);

	const textPlatonFeePercentage = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.platonFeePercentage`,
				"Estimated Platon commission in %",
			]),
		[t],
	);

	// platonAdditionalFee
	const platonAdditionalFeeValue = valueEditor.useGetter("platonAdditionalFee");
	const setPlatonAdditionalFeeValue = valueEditor.useSetter(
		"platonAdditionalFee",
	);
	const handlePlatonAdditionalFee = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setPlatonAdditionalFeeValue(value);
			}
		},
		[setPlatonAdditionalFeeValue],
	);

	const textPlatonAdditionalFee = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.platonAdditionalFee`,
				"Estimated Platon commission in UAH",
			]),
		[t],
	);

	const textPlatonFeeEstimateHint = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.platonFeeEstimateHint`,
				"Used for card hold/capture amount when commission is charged to the client. Actual fee comes from callback.",
			]),
		[t],
	);

	// systemFeePercentageForExecutor
	const systemFeePercentageForExecutorValue = valueEditor.useGetter(
		"systemFeePercentageForExecutor",
	);
	const setSystemFeePercentageForExecutorValue = valueEditor.useSetter(
		"systemFeePercentageForExecutor",
	);
	const handleSystemFeePercentageForExecutor = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setSystemFeePercentageForExecutorValue(value);
			}
		},
		[setSystemFeePercentageForExecutorValue],
	);

	const textSystemFeePercentageForExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.systemFeePercentageForExecutor`,
				"Additional driver commission in %",
			]),
		[t],
	);

	// systemFeeAdditionalForExecutor
	const systemFeeAdditionalForExecutorValue = valueEditor.useGetter(
		"systemFeeAdditionalForExecutor",
	);
	const setSystemFeeAdditionalForExecutorValue = valueEditor.useSetter(
		"systemFeeAdditionalForExecutor",
	);
	const handleSystemFeeAdditionalForExecutor = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setSystemFeeAdditionalForExecutorValue(value);
			}
		},
		[setSystemFeeAdditionalForExecutorValue],
	);

	const textSystemFeeAdditionalForExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.systemFeeAdditionalForExecutor`,
				"Additional commission from the driver in UAH.",
			]),
		[t],
	);

	// platonGlobalFeeFromSystem (toggle 1)
	const platonGlobalFeeFromSystemValue = valueEditor.useGetter(
		"platonGlobalFeeFromSystem",
	);
	const setPlatonGlobalFeeFromSystemValue = valueEditor.useSetter(
		"platonGlobalFeeFromSystem",
	);

	const showPlatonFeeEstimateWarning = useMemo(
		() =>
			!platonGlobalFeeFromSystemValue &&
			!platonFeePercentageValue &&
			!platonAdditionalFeeValue,
		[
			platonGlobalFeeFromSystemValue,
			platonFeePercentageValue,
			platonAdditionalFeeValue,
		],
	);

	// systemReturnFeeToExecutor
	const systemReturnFeeToExecutorValue = valueEditor.useGetter(
		"systemReturnFeeToExecutor",
	);
	const systemReturnFeeToExecutorOnChange = valueEditor.useSetter(
		"systemReturnFeeToExecutor",
	);

	const textGlobalFee = useMemo(() => {
		const fromClient = isCustomer
			? t([
					`${translationPath}.modal.text.client`,
					"Commission from the client",
			  ])
			: t([
					`${translationPath}.modal.text.client_executor`,
					"Commission from the client",
			  ]);

		const fromTaxiService =
			t(`${translationPath}.modal.text.commissionFromServiseTaxi`) ?? "";

		return platonGlobalFeeFromSystemValue ? fromTaxiService : fromClient;
	}, [platonGlobalFeeFromSystemValue, isCustomer, t]);

	const placeholderFeeToExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.label.feeToExecutor`,
				"Driver compensation",
			]),
		[t],
	);

	// platonPayoutToExecutorCardEnabled (toggle 2)
	const platonPayoutToExecutorCardValue = valueEditor.useGetter(
		"platonPayoutToExecutorCardEnabled",
	);
	const setPlatonPayoutToExecutorCardValue = valueEditor.useSetter(
		"platonPayoutToExecutorCardEnabled",
	);

	const textPayoutToExecutorCard = useMemo(
		() =>
			platonPayoutToExecutorCardValue
				? t([
						`${translationPath}.modal.text.platonPayoutToExecutorCardOn`,
						"Ride income goes to the driver (payout to card)",
				  ])
				: t([
						`${translationPath}.modal.text.platonPayoutToExecutorCardOff`,
						"Ride income goes to the taxi service",
				  ]),
		[platonPayoutToExecutorCardValue, t],
	);

	// platonAllowGooglePay
	const platonAllowGooglePayValue = valueEditor.useGetter(
		"platonAllowGooglePay",
	);
	const setPlatonAllowGooglePayValue = valueEditor.useSetter(
		"platonAllowGooglePay",
	);
	const textAllowGooglePay = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.platonAllowGooglePay`,
				"Allow Google Pay payments",
			]),
		[t],
	);

	// platonAllowApplePay
	const platonAllowApplePayValue = valueEditor.useGetter(
		"platonAllowApplePay",
	);
	const setPlatonAllowApplePayValue = valueEditor.useSetter(
		"platonAllowApplePay",
	);
	const textAllowApplePay = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.platonAllowApplePay`,
				"Allow Apple Pay payments",
			]),
		[t],
	);

	// Currency and CountryIso
	const textCurrency = useMemo(
		() => t([`${translationPath}.modal.text.currency`, "Currency"]),
		[t],
	);
	const textCountryIso = useMemo(
		() => t([`${translationPath}.modal.text.countryIso`, "Country"]),
		[t],
	);

	// controller
	controller.setContext({
		platonClientKey,
		platonClientPassword,
		platonApiUrl,
		currencyRef,
		countryIsoRef,
		setPlatonClientKeyError,
		setPlatonClientPasswordError,
		setPlatonApiUrlError,
	});

	return (
		<StyledColumn gap="10px" w="100%">
			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textPlatonClientKey} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderPlatonClientKey}
					error={textErrorPlatonClientKey}
					value={platonClientKey}
					onChange={handlePlatonClientKey}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textPlatonClientPassword} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderPlatonClientPassword}
					error={textErrorPlatonClientPassword}
					value={platonClientPassword}
					onChange={handlePlatonClientPassword}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textPlatonApiUrl} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderPlatonApiUrl}
					error={textErrorPlatonApiUrl}
					value={platonApiUrl}
					onChange={handlePlatonApiUrl}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textPlatonFeePercentage}:</StyledP>
				<ValueStepper
					width="80px"
					min={0}
					value={platonFeePercentageValue}
					onChange={handlePlatonFeePercentage}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textPlatonAdditionalFee}:</StyledP>
				<ValueStepper
					width="80px"
					min={0}
					value={platonAdditionalFeeValue}
					onChange={handlePlatonAdditionalFee}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px" />
				<StyledP style={{ fontSize: "12px", opacity: 0.85 }}>
					{textPlatonFeeEstimateHint}
				</StyledP>
			</StyledRow>

			{showPlatonFeeEstimateWarning && (
				<StyledRow alignItems="center" gap="10px" w="100%">
					<StyledP w="200px" />
					<StyledP style={{ fontSize: "12px", color: "#c77d00" }}>
						{t([
							`${translationPath}.modal.text.platonFeeEstimateWarning`,
							"Commission is charged to the client, but estimated fee is 0 — set % or fixed amount for hold/capture.",
						])}
					</StyledP>
				</StyledRow>
			)}

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px" />
				<StyledP style={{ fontSize: "12px", opacity: 0.85 }}>
					{textPlatonFeeFromCallbackHint}
				</StyledP>
			</StyledRow>

			{isExecutor && (
				<>
					<StyledRow alignItems="center" gap="10px" w="100%">
						<StyledP w="200px">
							{textSystemFeePercentageForExecutor}:
						</StyledP>
						<ValueStepper
							width="80px"
							min={0}
							value={systemFeePercentageForExecutorValue}
							onChange={handleSystemFeePercentageForExecutor}
						/>
					</StyledRow>

					<StyledRow alignItems="center" gap="10px" w="100%">
						<StyledP w="200px">
							{textSystemFeeAdditionalForExecutor}:
						</StyledP>
						<ValueStepper
							width="80px"
							min={0}
							value={systemFeeAdditionalForExecutorValue}
							onChange={handleSystemFeeAdditionalForExecutor}
						/>
					</StyledRow>
				</>
			)}

			<StyledRow alignItems="center" gap="10px">
				<StyledRow
					alignItems="center"
					gap="10px"
					onClick={(event) => event.stopPropagation()}
				>
					<ToggleButton.ToggleButton
						value={platonGlobalFeeFromSystemValue}
						onChange={setPlatonGlobalFeeFromSystemValue}
					/>
					<StyledP>{textGlobalFee}</StyledP>
				</StyledRow>
				<StyledRow alignItems="center" gap="10px" m="0 0 0 1rem">
					<CheckBox
						disabled={!platonGlobalFeeFromSystemValue}
						label={placeholderFeeToExecutor}
						value={systemReturnFeeToExecutorValue}
						onChange={systemReturnFeeToExecutorOnChange}
					/>
				</StyledRow>
			</StyledRow>

			{isCustomer && (
				<StyledRow
					alignItems="center"
					gap="10px"
					onClick={(event) => event.stopPropagation()}
				>
					<ToggleButton.ToggleButton
						value={platonPayoutToExecutorCardValue}
						onChange={setPlatonPayoutToExecutorCardValue}
					/>
					<StyledP>{textPayoutToExecutorCard}</StyledP>
				</StyledRow>
			)}

		<StyledRow
				alignItems="center"
				gap="10px"
				onClick={(event) => event.stopPropagation()}
			>
				<ToggleButton.ToggleButton
					value={platonAllowGooglePayValue}
					onChange={setPlatonAllowGooglePayValue}
				/>
				<StyledP>{textAllowGooglePay}</StyledP>
			</StyledRow>

			<StyledRow
				alignItems="center"
				gap="10px"
				onClick={(event) => event.stopPropagation()}
			>
				<ToggleButton.ToggleButton
					value={platonAllowApplePayValue}
					onChange={setPlatonAllowApplePayValue}
				/>
				<StyledP>{textAllowApplePay}</StyledP>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px">
				<StyledP w="200px">{textCurrency}:</StyledP>
				<PlatonPaymentsCurrency
					ref={setCurrencyRef}
					value={value}
					onChange={onChange}
				/>
			</StyledRow>
			<StyledRow alignItems="center" gap="10px">
				<StyledP w="200px">{textCountryIso}:</StyledP>
				<PlatonPaymentsCountryIso
					ref={setCountryIsoRef}
					value={value}
					onChange={onChange}
				/>
			</StyledRow>
		</StyledColumn>
	);
}, InternalController);

const PlatonPayment = memo(PlatonPaymentBase);

declare namespace PlatonPayment {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
		errors?: FieldErrors<any>;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default PlatonPayment;
